import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import type { KeyboardLike, KeyWith } from '@/lib/piano/keyboard';
import type { OptionalNumber } from '@/lib/piano/touch-design/measured-key.requirements';
export interface KeyboardStats {
  /** How many keys carry a value. */
  count: number;
  max: number | null;
  maxKeyName: string | null;
  mean: number | null;
  median: number | null;
  min: number | null;
  minKeyName: string | null;
  /** How many keys the instrument has. */
  size: number;
}
const EMPTY: Omit<KeyboardStats, 'size'> = {
  count: 0,
  max: null,
  maxKeyName: null,
  mean: null,
  median: null,
  min: null,
  minKeyName: null
};

/**
 * The spread of one quantity across the keyboard.
 *
 * The median is computed here rather than in the chart, because the chart
 * draws it and the summary card states it: one number, one source.
 */
export const useKeyboardStats = (keyboard, selector) => {
  const $ = _c(7);
  let t0;
  if ($[0] !== keyboard || $[1] !== selector) {
    bb0: {
      let t1;
      if ($[3] !== selector) {
        t1 = key => ({
          name: key.name,
          value: selector(key)
        });
        $[3] = selector;
        $[4] = t1;
      } else {
        t1 = $[4];
      }
      const measured = keyboard.getKeys().map(t1).filter(_temp);
      if (measured.length === 0) {
        let t2;
        if ($[5] !== keyboard.size) {
          t2 = {
            ...EMPTY,
            size: keyboard.size
          };
          $[5] = keyboard.size;
          $[6] = t2;
        } else {
          t2 = $[6];
        }
        t0 = t2;
        break bb0;
      }
      const sorted = [...measured].sort(_temp2);
      const lowest = sorted[0];
      const highest = sorted[sorted.length - 1];
      const middle = Math.floor(sorted.length / 2);
      t0 = {
        count: measured.length,
        max: highest.value,
        maxKeyName: highest.name,
        mean: measured.reduce(_temp3, 0) / measured.length,
        median: sorted.length % 2 === 0 ? (sorted[middle - 1].value + sorted[middle].value) / 2 : sorted[middle].value,
        min: lowest.value,
        minKeyName: lowest.name,
        size: keyboard.size
      };
    }
    $[0] = keyboard;
    $[1] = selector;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
function _temp(entry) {
  return entry.value !== null && !Number.isNaN(entry.value);
}
function _temp2(a, b) {
  return a.value - b.value;
}
function _temp3(total, entry_0) {
  return total + entry_0.value;
}